import { ShippingProfileEntity } from '../shippingProfile/types';
import { EntityState } from '../types';

export enum ShipperEnvSwitchStatus {
  SWITCH_PENDING = 'pending',
  SWITCH_COMPLETE = 'switchComplete',
}

interface LastKnownLocation {
  lat: number;
  long: number;
  timestamp: string;
}

interface LastKnownSensorData {
  battery: number;
  tempC: number;
  timestamp: string;
}

export enum EnvEnum {
  PRODUCTION = 'production',
  STAGING = 'staging',
  DEVELOPMENT = 'development',
}

export enum DeviceType {
  MedstowMicro = 'Medstow Micro',
  Medstow5LI = 'Medstow 5L-I',
}

export enum DeviceDeploymentStatus {
  DEPLOYED = 'Deployed',
  ON_STANDBY = 'On Standby',
  READY = 'Ready',
  // A calibration was recorded as failed; the unit is blocked from deployment
  // until it is successfully recalibrated.
  NEEDS_RECALIBRATION = 'Needs Recalibration',
}

export type DeviceMode =
  | 'normal'
  | 'calibration'
  | 'test'
  | 'excursion-test'
  | 'eol-test'
  | 'pending';

export type CalibrationStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'READINGS_ENTERED'
  | 'COMPLETE'
  | 'FAILED'
  | 'CANCELED';

interface LastHealthCheck {
  pluggedInAt: string;
  coolingPass: boolean | null;
  fullyCharged: boolean | null;
  cellConnected: boolean | null;
  gpsConnected: boolean | null;
  wifiConnected: boolean | null;
}

interface ReadyStatusInfo {
  isReady: boolean;
  lastUpdated: string;
  pluginTimestamp?: string;
}

export interface DeviceEntity {
  _id: string;
  deviceType: DeviceType;
  serialNumber: string;
  companyName: string;
  companyId: string;
  groupId?: string | null;
  groupName?: string | null;
  pcbNumber: string;
  boardVersion: string;
  imei: string;
  macAddress: string;
  currentEnvironment: EnvEnum;
  envSwitchStatus: ShipperEnvSwitchStatus;
  mostRecentJourney: string;
  deviceDeploymentStatus: DeviceDeploymentStatus;
  lastKnownLocation: LastKnownLocation;
  lastKnownSensorData: LastKnownSensorData;
  newEnvironment?: EnvEnum | null;
  isInCurrentEnv: boolean;
  firmwareVersion?: string;
  lastHealthCheck?: LastHealthCheck;
  cellEnabled: boolean;
  updatedAt: string;
  isCompanySwitchPending: boolean;
  shippingProfileDoc?: ShippingProfileEntity;
  // Per-unit override of which company shipping profiles the unit exposes on
  // its LCD; null/absent = all active company profiles.
  enabledShippingProfiles?: string[] | null;
  profileConfigSync?: {
    status: 'pending' | 'applied';
    lastUpdated: string;
  } | null;
  readyStatusInfo?: ReadyStatusInfo;
  isReady?: boolean;
  deviceMode?: DeviceMode;
  systemMode?: 'NORMAL' | 'CALIBRATION' | 'EXCURSION' | 'EOL';
  calibrationStatus?: CalibrationStatus | null;
  assignedFieldPersonnelId?: string | null;
  assignedFieldPersonnelName?: string | null;
  // Wi-Fi networks the unit holds (max 5, firmware limit).
  wifiNetworks?: {
    wifiNetwork?: string | null;
    ssid: string;
    label?: string;
    pushedAt: string;
    pushedBy: string;
  }[];
}

export interface DeviceFilters {
  search?: string;
  companyIds?: string[];
  excludeCompanyIds?: string[];
  deviceTypes?: DeviceType[];
  isCompanySwitchPendingFilter?: boolean;
  needsCalibrationReadingsFilter?: boolean;
  needsRecalibrationFilter?: boolean;
  needsCertificateFilter?: boolean;
  profileIds?: string[];
  modes?: DeviceMode[];
}

export interface DeviceState extends EntityState<DeviceEntity> {
  selectedFilters?: DeviceFilters;
  deviceTypeList: DeviceType[] | null;
  totalCompanySwitchPending: number;
  totalCalibrationsNeedingReadings: number;
  totalDevicesNeedingRecalibration: number;
  totalDevicesPendingCertificate: number;
}
